import { BiChevronLeft } from "react-icons/bi";
import { BsTelephone } from "react-icons/bs";
import { useNavigate, useParams } from "react-router";
import { TiUserOutline } from "react-icons/ti";
import { GrLocation } from "react-icons/gr";
import { useMutation, useQuery, useQueryClient } from "react-query";
import { getUserDetail, manageUserStatus, setUserRisk } from "../../api/user";
import { getUserOrders, getUserTransactions } from "../../api/order";
import { getWithdrawals } from "../../api/withdrawal";
import { orderColumn, transactionColumn } from "./column";
import { withdrawalColumns } from "../withdrawals/column";
import { Input, Select, Table, Tabs } from "antd";
import { showErrorToast, showSuccessToast } from "../../utils/toast";
import { useState } from "react";
import RiskBadge from "../shared/RiskBadge";
import CustomerMonitoring from "../monitoring/CustomerMonitoring";
import { usePermissionEffect } from "../../utils/usePermissionEffect";
import VerifyBvnButton from "./VerifyBvnButton";

const items = [
  {
    key: "1",
    label: "Transactions",
    children: <CustomerDetailsTransactions />,
  },
  {
    key: "2",
    label: "Withdrawals",
    children: <CustomerDetailsWithdrawals />,
  },
  {
    key: "3",
    label: "Order History",
    children: <CustomerDetailsOrderHistory />,
  },
  {
    key: "4",
    label: "Monitoring",
    children: <CustomerMonitoring />,
  },
];

const boxShadowStyle = {
  boxShadow: "0px 2px 9px 0px #00000008",
  userSelect: "none",
};

const CustomerDetails = () => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { id } = useParams();

  const { data } = useQuery(["user-detail", id], () => getUserDetail(id));

  const mutation = useMutation(manageUserStatus, {
    onSuccess: () => {
      showSuccessToast(
        `${data?.profile?.firstName} has been ${
          data?.profile?.isActive ? "deactivated" : "activated"
        }`,
      );
      queryClient.invalidateQueries("user-detail");
    },
    onError: (e) => {
      showErrorToast(e.message);
    },
    onMutate: () =>
      showSuccessToast(
        `${data?.profile?.isActive ? "Deactivating..." : "Activating..."}`,
      ),
  });

  const onManageStatus = () => {
    mutation.mutate({
      isActive: !data?.profile?.isActive || false,
      id: data?.profile?._id,
    });
  };

  return (
    <div className="p-5 font-dmsans">
      <div className="flex flex-col gap-5 mb-16">
        <div className="flex items-center gap-3">
          <BiChevronLeft
            className="text-[#070E05] text-[25px] cursor-pointer"
            onClick={() => {
              navigate(`/customers/`);
            }}
          />
          <div className="font-dmsans font-bold text-[20px] text-[#070E05]">
            Customer Details
          </div>
        </div>
        {data?.profile?.watchlistFlag?.flagged && (
          <div className="bg-[#EB57571A] border border-[#EB5757] text-[#EB5757] rounded-lg px-5 py-3 text-[14px]">
            <b>BVN watchlist match.</b> This customer's BVN (ending{" "}
            {data.profile.watchlistFlag.bvnLast4}) is on the watchlist
            {data.profile.watchlistFlag.reason ? ` — ${data.profile.watchlistFlag.reason}` : ""}. The account was
            deactivated on {new Date(data.profile.watchlistFlag.flaggedAt).toLocaleDateString()}. Only reactivate after
            compliance review.
          </div>
        )}
        {data?.profile?.duplicateBvnFlag?.flagged && (
          <div className="bg-[#F2994A1A] border border-[#F2994A] text-[#B4621B] rounded-lg px-5 py-3 text-[14px]">
            <b>Duplicate BVN.</b> This customer's BVN (ending {data.profile.duplicateBvnFlag.bvnLast4}) is also on{" "}
            <span
              className="underline cursor-pointer"
              onClick={() => navigate(`/customers/${data.profile.duplicateBvnFlag.keptUserId}`)}
            >
              another account
            </span>
            , which kept it. This account was deactivated on{" "}
            {new Date(data.profile.duplicateBvnFlag.flaggedAt).toLocaleDateString()}.
          </div>
        )}
        <div className="w-full flex gap-4">
          <div className="w-1/3 bg-white p-8 rounded-lg h-fit">
            <div className="relative flex">
              <div className="m-auto h-32 w-32 bg-black rounded-full"></div>
              <div
                className={`${
                  data?.profile?.isActive ? "bg-[#27AE601A]" : "bg-[#cd450f1a]"
                } rounded-[3px] h-fit absolute right-0 top-[-10px] py-[3px] px-[10px] ${
                  data?.profile?.isActive ? "text-[#219653]" : "text-[#fff]"
                } text-[13px]`}
              >
                {data?.profile?.isActive ? "Active" : "In Active"}
              </div>
            </div>
            <div className="mt-10 mb-20">
              <div className="flex flex-col gap-7">
                <div className="flex gap-3 text-[15px] items-center">
                  <TiUserOutline className="text-[23px]" />
                  <div className="font-bold">
                    Full Name:{" "}
                    <span className="font-normal capitalize">
                      {data?.profile?.firstName} {data?.profile?.lastName}
                    </span>
                  </div>
                </div>
                <div className="flex gap-3 text-[15px] items-center">
                  <svg
                    width="24"
                    height="24"
                    className="w-[23px]"
                    viewBox="0 0 24 24"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      d="M17.9026 8.8512L13.4593 12.4642C12.6198 13.1302 11.4387 13.1302 10.5992 12.4642L6.11841 8.8512"
                      stroke="#130F26"
                      stroke-width="1.5"
                      stroke-linecap="round"
                      stroke-linejoin="round"
                    />
                    <path
                      fill-rule="evenodd"
                      clip-rule="evenodd"
                      d="M16.9089 21C19.9502 21.0084 22 18.5095 22 15.4384V8.57001C22 5.49883 19.9502 3 16.9089 3H7.09114C4.04979 3 2 5.49883 2 8.57001V15.4384C2 18.5095 4.04979 21.0084 7.09114 21H16.9089Z"
                      stroke="#130F26"
                      stroke-width="1.5"
                      stroke-linecap="round"
                      stroke-linejoin="round"
                    />
                  </svg>
                  <div className="font-bold">
                    Email:{" "}
                    <span className="font-normal">{data?.profile?.email}</span>
                  </div>
                </div>
                <div className="flex gap-3 text-[15px] items-center">
                  <BsTelephone className="text-[23px]" />
                  <div className="font-bold">
                    Phone Number:{" "}
                    <span className="font-normal">
                      {data?.profile?.phoneNumber}
                    </span>{" "}
                    <span
                      className={`text-[12px] font-normal ${
                        data?.profile?.isPhoneVerified
                          ? "text-[#219653]"
                          : "text-[#EB5757]"
                      }`}
                    >
                      {data?.profile?.isPhoneVerified ? "Verified" : "Not verified"}
                    </span>
                  </div>
                </div>
                <div className="flex gap-3 text-[15px] items-center">
                  <GrLocation className="text-[23px]" />
                  <div className="font-bold">
                    Location:{" "}
                    <span className="font-normal">
                      {data?.location || data?.profile?.address}
                    </span>
                  </div>
                </div>
                <div className="flex gap-3 text-[15px] items-center">
                  <TiUserOutline className="text-[23px]" />
                  <div className="font-bold">
                    BVN:{" "}
                    <span
                      className={`font-normal ${
                        data?.activity?.isBvnVerified ? "text-[#219653]" : "text-[#EB5757]"
                      }`}
                    >
                      {data?.activity?.isBvnVerified
                        ? "Verified"
                        : data?.activity?.hasBvn
                          ? "Not verified"
                          : "Not submitted"}
                    </span>
                  </div>
                  {data?.activity && !data.activity.isBvnVerified && (
                    <VerifyBvnButton
                      size="small"
                      userId={data?.profile?._id}
                      name={data?.profile?.firstName}
                      hasBvn={data.activity.hasBvn}
                    />
                  )}
                </div>
                <div className="flex gap-3 text-[15px] items-center">
                  <TiUserOutline className="text-[23px]" />
                  <div className="font-bold">
                    Referrals:{" "}
                    <span className="font-normal">
                      {Number(data?.profile?.referralsCount || 0).toLocaleString()}
                    </span>
                  </div>
                </div>
              </div>
            </div>
            <div className="mt-auto border-[#BDBDBD] border-t-[1px] pt-5 px-3">
              <button
                onClick={onManageStatus}
                type="button"
                className="text-[#EB5757] bg-[#FF3A441A] rounded-[5px] px-[15px] font-bold py-[10px] w-fit"
              >
                {data?.profile?.isActive
                  ? "Deactivate Customer"
                  : "Activate Customer"}
              </button>
            </div>
          </div>
          <div className="w-2/3 flex flex-col gap-4">
            <div className="w-full bg-white py-4 px-6 flex flex-col gap-3 rounded-lg">
              <div className="font-bold text-[14px] text-[#181819E5]">
                Customer Holdings
              </div>
              <div className="flex justify-between">
                <div>
                  <div className="text-[12px] text-[#00000080]">
                    Wallet Balance
                  </div>
                  <div className="font-bold text-[#14142B] text-[26px]">
                    N{" "}
                    {Number(data?.holdings?.fiatBalance || 0).toLocaleString()}
                  </div>
                </div>

                <div>
                  <div className="text-[12px] text-[#00000080]">
                    BTC Balance
                  </div>
                  <div className="font-bold text-[#14142B] text-[26px]">
                    {new Intl.NumberFormat("en-US", {
                      minimumFractionDigits: 0,
                      maximumFractionDigits: 8,
                    }).format(Number(data?.holdings?.btcBalance || 0))}
                  </div>
                </div>

                <div>
                  <div className="text-[12px] text-[#00000080]">
                    USDT Balance
                  </div>
                  <div className="font-bold text-[#14142B] text-[26px]">
                    {new Intl.NumberFormat("en-US", {
                      minimumFractionDigits: 0,
                      maximumFractionDigits: 8,
                    }).format(Number(data?.holdings?.usdtBalance || 0))}
                  </div>
                </div>

                <div>
                  <div className="text-[12px] text-[#00000080]">
                    USDC Balance
                  </div>
                  <div className="font-bold text-[#14142B] text-[26px]">
                    {new Intl.NumberFormat("en-US", {
                      minimumFractionDigits: 0,
                      maximumFractionDigits: 8,
                    }).format(Number(data?.holdings?.usdcBalance || 0))}
                  </div>
                </div>
              </div>
            </div>
            <CustomerRisk
              userId={data?.profile?._id}
              risk={data?.risk}
              activity={data?.activity}
            />
            <div className="w-full h-full bg-white py-4 px-6 flex flex-col gap-6 rounded-lg">
              <Tabs defaultActiveKey="1" items={items} />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CustomerDetails;

export function CustomerDetailsOrderHistory() {
  const { id } = useParams();

  const { data } = useQuery(["user-orders", id], () => getUserOrders(id));
  return (
    <div className="table-container w-full">
      <Table dataSource={data?.orders || []} columns={orderColumn} />
    </div>
  );
}

export function CustomerDetailsTransactions() {
  const { id } = useParams();
  const [page, setPage] = useState(1);

  const { data, isFetching } = useQuery(["user-trans", id, page], () =>
    getUserTransactions(id, page),
  );

  return (
    <div className="table-container w-full">
      <Table
        dataSource={data?.transactions || []}
        columns={transactionColumn}
        loading={isFetching}
        pagination={{
          current: page,
          pageSize: 10,
          total: data?.total || 1,
          showSizeChanger: true,
          onChange: (newPage) => {
            setPage(newPage);
          },
        }}
      />
    </div>
  );
}

const naira = (n) => `₦${Number(n || 0).toLocaleString()}`;

function CustomerRisk({ userId, risk, activity }) {
  const queryClient = useQueryClient();
  const { getModulePermission } = usePermissionEffect();
  const [level, setLevel] = useState();
  const [note, setNote] = useState("");

  const mutation = useMutation(setUserRisk, {
    onSuccess: () => {
      showSuccessToast("Risk level updated");
      setLevel(undefined);
      setNote("");
      queryClient.invalidateQueries("user-detail");
    },
    onError: (e) => showErrorToast(e.message),
  });

  if (!risk) return null;

  const stats = [
    ["Deposits", naira(activity?.depositAmount)],
    ["Withdrawals", naira(activity?.payoutAmount)],
    ["Withdrawals (24h)", `${activity?.payoutCount24h || 0} · ${naira(activity?.payoutAmount24h)}`],
    ["Failed transactions", activity?.failedCount || 0],
    ["Declined gift cards", activity?.giftCardDeclined || 0],
    ["BVN", activity?.isBvnVerified ? "Verified" : "Not verified"],
  ];

  return (
    <div className="w-full bg-white py-4 px-6 flex flex-col gap-4 rounded-lg">
      <div className="flex items-center justify-between">
        <div className="font-bold text-[14px] text-[#181819E5]">Risk</div>
        <div className="flex items-center gap-3 text-[13px] text-[#00000080]">
          Score {risk.score}/100
          <RiskBadge risk={risk} />
        </div>
      </div>

      {risk.override && (
        <div className="text-[13px] text-[#00000080]">
          Set to <b className="capitalize">{risk.override.level}</b> by{" "}
          {risk.override.setBy || "an admin"}
          {risk.override.setAt &&
            ` on ${new Date(risk.override.setAt).toLocaleDateString()}`}
          {risk.override.note && ` — "${risk.override.note}"`}. Computed level:{" "}
          <span className="capitalize">{risk.computedLevel}</span>.
        </div>
      )}

      <div className="grid grid-cols-3 gap-3">
        {stats.map(([label, value]) => (
          <div key={label}>
            <div className="text-[12px] text-[#00000080]">{label}</div>
            <div className="font-bold text-[#14142B] text-[15px]">{value}</div>
          </div>
        ))}
      </div>

      <div>
        <div className="text-[12px] text-[#00000080] mb-1">Why</div>
        {risk.reasons.length ? (
          <ul className="list-disc pl-5 text-[13px] space-y-1">
            {risk.reasons.map((r) => (
              <li key={r.reason}>
                {r.reason} <span className="text-[#00000080]">(+{r.points})</span>
              </li>
            ))}
          </ul>
        ) : (
          <div className="text-[13px]">No risk signals found.</div>
        )}
      </div>

      {getModulePermission("customer", "canUpdate") && (
        <div className="flex gap-3 items-center border-t border-[#E6E7E9] pt-4">
          <Select
            placeholder="Set risk level"
            value={level}
            onChange={setLevel}
            className="w-[180px]"
            options={[
              { value: "low", label: "Low" },
              { value: "medium", label: "Medium" },
              { value: "high", label: "High" },
              { value: "auto", label: "Automatic (clear)" },
            ]}
          />
          <Input
            placeholder="Note (optional)"
            value={note}
            onChange={(e) => setNote(e.target.value)}
            maxLength={200}
          />
          <button
            type="button"
            disabled={!level || mutation.isLoading}
            onClick={() => mutation.mutate({ id: userId, level, note })}
            className="bg-dark text-white rounded-[5px] px-4 py-[6px] font-bold text-[14px] disabled:opacity-50 whitespace-nowrap"
          >
            {mutation.isLoading ? "Saving..." : "Save"}
          </button>
        </div>
      )}
    </div>
  );
}

export function CustomerDetailsWithdrawals() {
  const { id } = useParams();
  const [page, setPage] = useState(1);
  const { getModulePermission } = usePermissionEffect();
  const canView = getModulePermission("Withdrawal", "canView");

  const { data, isFetching } = useQuery(
    ["user-withdrawals", id, page],
    () => getWithdrawals({ userId: id, kind: "all", page, limit: 10 }),
    { enabled: canView },
  );

  if (!canView) {
    return <div className="text-[13px] text-[#00000080]">You need the Withdrawal permission to see this.</div>;
  }

  return (
    <div className="table-container w-full">
      <Table
        rowKey="_id"
        dataSource={data?.withdrawals || []}
        columns={withdrawalColumns({ showCustomer: false })}
        loading={isFetching}
        pagination={{
          current: page,
          pageSize: 10,
          total: data?.total || 1,
          onChange: setPage,
        }}
      />
    </div>
  );
}
